import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from './api';

// ── Carga de datos ─────────────────────────────────────────────────────────

export interface AsyncState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => Promise<void>;
  setData: (d: T) => void;
}

/**
 * Carga algo al montar y cada vez que cambian `deps`. Si una carga lenta
 * termina después de otra más reciente, su resultado se descarta: si no, al
 * cambiar rápido de cliente en el panel se vería el último que TERMINÓ, no el
 * último que se PIDIÓ.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fn();
      if (mine === seq.current) setData(result);
    } catch (err) {
      if (mine === seq.current) setError(err);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, error, loading, reload, setData };
}

// ── Estados ───────────────────────────────────────────────────────────────

export function Loading({ label = 'Cargando…' }: { label?: string }): JSX.Element {
  return (
    <div className="loading" role="status">
      <span className="spinner" /> {label}
    </div>
  );
}

/** Error de la API dicho para personas: mensaje y, si lo hay, qué hacer. */
export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }): JSX.Element | null {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  const hint = error instanceof ApiError ? error.hint : null;
  return (
    <div className="callout bad" role="alert">
      <div className="title">{message}</div>
      {hint ? <div className="muted">{hint}</div> : null}
      {onRetry ? (
        <div style={{ marginTop: 8 }}>
          <button className="btn sm" onClick={onRetry}>
            Reintentar
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Empty({ icon = '∅', children }: { icon?: string; children: ReactNode }): JSX.Element {
  return (
    <div className="empty">
      <div className="icon" aria-hidden="true">
        {icon}
      </div>
      {children}
    </div>
  );
}

export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'accent' | 'neutral';

export function Badge({ tone = 'neutral', children, plain }: { tone?: Tone; children: ReactNode; plain?: boolean }): JSX.Element {
  return <span className={`badge ${tone === 'neutral' ? '' : tone} ${plain ? 'plain' : ''}`}>{children}</span>;
}

export function Stat({
  label,
  value,
  foot,
  tone,
}: {
  label: string;
  value: ReactNode;
  foot?: ReactNode;
  tone?: 'ok' | 'warn' | 'bad';
}): JSX.Element {
  return (
    <div className={`card stat ${tone ?? ''}`}>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {foot ? <div className="foot">{foot}</div> : null}
    </div>
  );
}

export function Card({
  title,
  actions,
  children,
  bodyless,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** Sin relleno interior: para tablas que llegan al borde. */
  bodyless?: boolean;
}): JSX.Element {
  return (
    <section className="card">
      {title || actions ? (
        <div className="card-head">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {actions ? <div className="row">{actions}</div> : null}
        </div>
      ) : null}
      {bodyless ? children : <div className="card-body">{children}</div>}
    </section>
  );
}

// ── Formularios ───────────────────────────────────────────────────────────

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {error ? <div className="error">{error}</div> : hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<{ id: T; label: ReactNode }>;
  value: T;
  onChange: (id: T) => void;
}): JSX.Element {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.id} role="tab" aria-selected={value === t.id} className={value === t.id ? 'active' : ''} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true">
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn ghost sm" onClick={onClose} aria-label="Cerrar">
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/**
 * Confirmación antes de algo que no se deshace. `requireText` obliga a
 * escribir algo (p. ej. el identificador del cliente) para borrar.
 */
export function Confirm({
  title,
  message,
  confirmLabel = 'Confirmar',
  danger,
  requireText,
  onConfirm,
  onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  requireText?: string;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const blocked = requireText !== undefined && text.trim() !== requireText;
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            className={`btn ${danger ? 'solid-danger' : 'primary'}`}
            disabled={busy || blocked}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onConfirm();
                onClose();
              } catch (err) {
                setError(err);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Un momento…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="stack">
        <div>{message}</div>
        {requireText !== undefined ? (
          <Field label={`Escribe «${requireText}» para confirmar`}>
            <input className="input" value={text} onChange={(e) => setText(e.target.value)} autoFocus />
          </Field>
        ) : null}
        <ErrorBox error={error} />
      </div>
    </Modal>
  );
}

/** Una contraseña que se enseña UNA SOLA VEZ, con botón de copiar. */
export function OneTimeSecret({ label, value, note }: { label: string; value: string; note?: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="stack sm">
      <div className="field">
        <span className="label">{label}</span>
        <div className="secret">{value}</div>
      </div>
      <div className="row between">
        <span className="subtle">{note ?? 'No se puede volver a consultar. Si se pierde, se genera otra.'}</span>
        <button
          className="btn sm"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copiada' : 'Copiar'}
        </button>
      </div>
    </div>
  );
}

// ── Notificaciones ────────────────────────────────────────────────────────

interface ToastItem {
  id: number;
  tone: 'ok' | 'bad' | 'neutral';
  text: string;
}

const ToastContext = createContext<(text: string, tone?: ToastItem['tone']) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const push = useCallback((text: string, tone: ToastItem['tone'] = 'neutral') => {
    const id = next.current++;
    setItems((list) => [...list, { id, tone, text }]);
    window.setTimeout(() => setItems((list) => list.filter((t) => t.id !== id)), tone === 'bad' ? 8000 : 4000);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.tone === 'neutral' ? '' : t.tone}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (text: string, tone?: 'ok' | 'bad' | 'neutral') => void {
  return useContext(ToastContext);
}

/** Mensaje de un error para un toast. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.hint ? `${err.message}. ${err.hint}` : err.message;
  return err instanceof Error ? err.message : String(err);
}

// ── Tema ──────────────────────────────────────────────────────────────────

export function ThemeSelect({ className }: { className?: string }): JSX.Element {
  const [theme, setTheme] = useState<string>(() => {
    try {
      return localStorage.getItem('provalia.theme') ?? 'auto';
    } catch {
      return 'auto';
    }
  });
  useEffect(() => {
    if (theme === 'auto') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('provalia.theme', theme);
    } catch {
      /* preferencia solo de esta visita */
    }
  }, [theme]);
  return (
    <select className={`select ${className ?? ''}`} style={{ width: 'auto', minHeight: 30, padding: '3px 8px' }} value={theme} onChange={(e) => setTheme(e.target.value)} aria-label="Tema">
      <option value="auto">Tema del sistema</option>
      <option value="light">Claro</option>
      <option value="dark">Oscuro</option>
    </select>
  );
}
